import { INodeCreate, INodeCreateFormNew, IOrganizationChartNode } from '../../../../../app/resource/node/node.types';
import { chartNodePrimaryParentId } from '../../../../../app/resource/node/node.utils';
import {
  H_GAP,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROOT_Y,
  V_GAP,
  getNodePosition,
  rightOfRightmost,
} from '../../../../../app/resource/node/node.utils';
import { NodePosition } from '../../../../../app/resource/node/node.types';

/** Treat y as on the root row when coordinates are integers from layout/API. */
const ROOT_ROW_Y_TOLERANCE = 0.5;

export type CreateNodePlacement = {
  createPosition: { positionX: number; positionY: number };
};

/**
 * Extracts normalized parent ids from the node form payload.
 *
 * The form can provide `parents` in different states (undefined, null, or a list
 * of option-like objects). This helper guarantees a stable string array so the
 * rest of the placement logic can treat ids consistently.
 *
 * - Returns an empty array when the value is missing or invalid.
 * - Converts every selected value to string to avoid number/string mismatches.
 * - Drops empty string values to avoid creating invalid parent references.
 */
export function parentIdsFromForm(parents: INodeCreateFormNew['parents']): string[] {
  if (!parents || !Array.isArray(parents)) {
    return [];
  }
  return parents.map((p) => String(p.value)).filter((v) => v !== '');
}

/**
 * Maps the optional external user block from the form to the API contract.
 *
 * The form allows creating a node with an embedded external user. This utility
 * translates that UI shape into the `INodeCreate` payload while keeping the
 * property optional.
 *
 * - Returns `undefined` when no external user was provided.
 * - Preserves first and last name as entered in the form.
 * - Normalizes a falsy `image` value to `undefined` so the payload does not send
 *   empty image values.
 */
export function externalUserFromForm(externalUser: INodeCreateFormNew['externalUser']): INodeCreate['externalUser'] {
  return externalUser
    ? {
        firstName: externalUser.firstName,
        lastName: externalUser.lastName || undefined,
        image: externalUser.image || undefined,
      }
    : undefined;
}

/**
 * Builds lookup maps used by placement.
 *
 * - `nodeById`: direct access to any node by id.
 * - `childIdsByPrimaryParent`: child lists keyed by primary parent id.
 * - `rootIds`: nodes that do not have a valid parent within the current graph.
 *
 * Child and root ids are sorted to keep deterministic placement results across
 * runs, even when input order changes.
 */
function buildNodeMaps(
  nodes: IOrganizationChartNode[],
): {
  nodeById: Map<string, IOrganizationChartNode>;
  childIdsByPrimaryParent: Map<string, string[]>;
  rootIds: string[];
} {
  const nodeById = new Map(nodes.map((node) => [String(node.id), node] as const));
  const childIdsByPrimaryParent = new Map<string, string[]>();

  for (const node of nodes) {
    const parentId = chartNodePrimaryParentId(node);
    if (!parentId || !nodeById.has(String(parentId))) {
      continue;
    }
    const parentKey = String(parentId);
    const current = childIdsByPrimaryParent.get(parentKey);
    if (current) {
      current.push(String(node.id));
    } else {
      childIdsByPrimaryParent.set(parentKey, [String(node.id)]);
    }
  }

  childIdsByPrimaryParent.forEach((ids, parentId) => {
    childIdsByPrimaryParent.set(
      parentId,
      ids.sort((a, b) => a.localeCompare(b)),
    );
  });

  const rootIds = nodes
    .filter((node) => {
      const parentId = chartNodePrimaryParentId(node);
      return !parentId || !nodeById.has(String(parentId));
    })
    .map((node) => String(node.id))
    .sort((a, b) => a.localeCompare(b));

  return { nodeById, childIdsByPrimaryParent, rootIds };
}

function rightmostX(positions: NodePosition[]): NodePosition {
  return positions.reduce((max, current) => (current.x > max.x ? current : max), positions[0]);
}

/**
 * Computes the next available root position.
 *
 * Root nodes live on the same baseline (`ROOT_Y`). Prefer positions of
 * structural roots (`rootIds`); if none are known (e.g. every node has a
 * parent id in the graph so `rootIds` is empty), fall back to the rightmost
 * node on the root row, then to the rightmost node overall. This avoids
 * stacking every new parentless node at the origin.
 */
function nextRootPosition(rootIds: string[], positionedById: Map<string, NodePosition>): NodePosition {
  const fromStructuralRoots = rootIds
    .map((id) => positionedById.get(id))
    .filter((pos): pos is NodePosition => pos !== undefined);

  const all = Array.from(positionedById.values());
  const onRootRow = all.filter((p) => Math.abs(p.y - ROOT_Y) <= ROOT_ROW_Y_TOLERANCE);

  let candidates: NodePosition[];
  if (fromStructuralRoots.length > 0) {
    candidates = fromStructuralRoots;
  } else if (onRootRow.length > 0) {
    candidates = onRootRow;
  } else {
    candidates = all;
  }

  if (candidates.length === 0) {
    return { x: 0, y: ROOT_Y };
  }

  const rightmost = rightmostX(candidates);
  return { x: rightmost.x + NODE_WIDTH + H_GAP, y: ROOT_Y };
}

/**
 * Computes where a newly created node should be placed in the chart.
 *
 * - No valid parent: place as a new root, to the right of existing roots.
 * - Valid parent: place to the right of current children, or directly below the
 *   parent when it has no children yet.
 *
 * Assumes every existing chart node has persisted `positionX` / `positionY`.
 */
export function computeCreateNodePlacement(nodes: IOrganizationChartNode[], parentIds: string[]): CreateNodePlacement {
  const primaryParentId = parentIds[0] ? String(parentIds[0]) : undefined;
  const { nodeById, childIdsByPrimaryParent, rootIds } = buildNodeMaps(nodes);
  const positionedById = new Map<string, NodePosition>();

  for (const node of nodes) {
    positionedById.set(String(node.id), getNodePosition(node));
  }

  if (primaryParentId) {
    const parentNode = nodeById.get(primaryParentId);
    if (parentNode) {
      const parentPosition = getNodePosition(parentNode);

      const childIds = childIdsByPrimaryParent.get(primaryParentId) || [];
      const childPositions = childIds
        .map((id) => nodeById.get(id))
        .filter((node): node is IOrganizationChartNode => node !== undefined)
        .map((node) => getNodePosition(node));

      const createPosition =
        childPositions.length > 0
          ? rightOfRightmost(childPositions)
          : { x: parentPosition.x, y: parentPosition.y + NODE_HEIGHT + V_GAP };

      return {
        createPosition: {
          positionX: createPosition.x,
          positionY: createPosition.y,
        },
      };
    }
  }

  const createPosition = nextRootPosition(rootIds, positionedById);

  return {
    createPosition: {
      positionX: createPosition.x,
      positionY: createPosition.y,
    },
  };
}
